import { createRootRoute, Link, Outlet, useNavigate } from '@tanstack/react-router';
import {
  AirplaneTiltIcon,
  CaretDownIcon,
  GearSixIcon,
  MapTrifoldIcon,
  NotebookIcon,
} from '@phosphor-icons/react';
import { useEffect, useState } from 'react';

import { ICON_WEIGHT } from '@/components/icon';
import { Popover } from '@/components/popover';
import { Button } from '@/components/ui/button';
import { AutoImportBanner } from '@/features/auto-import';
import { DropZone, ImportButton, ImportQueuePanel, useImportQueue } from '@/features/import';
import { useDraftCount } from '@/features/logbook';
import {
  currentBrowserEnvironment,
  detectStorageRisk,
  StorageNoticeDialog,
} from '@/features/storage-notice';
import { SyncStatusLine, syncNow, useAutoSync } from '@/features/sync';
import { useShouldShowWelcome, WelcomeDialog } from '@/features/welcome';
import { isTauri } from '@/lib/platform';
import { checkForDesktopUpdate, startDesktopBridge } from '@/desktop';
import { readLicense } from '@/features/license';
import { collectSharedFiles, consumeLaunchQueue } from '@/pwa';

import { openFiles } from '../open-files';
import { OpenedFileNotice } from '../OpenedFileNotice';
import { useShortcutsDialog } from '../shortcuts-dialog-store';
import { ShortcutsDialog } from '../ShortcutsDialog';
import { UpdateBanner } from '../UpdateBanner';
import { useKeyboardShortcuts } from '../use-keyboard-shortcuts';

/** Three primary destinations. In the header from `sm` up, in a bottom tab bar below it. */
const NAV = [
  { to: '/flights', label: 'Flights', icon: MapTrifoldIcon },
  { to: '/logbook', label: 'Logbook', icon: NotebookIcon },
  { to: '/aircraft', label: 'Aircraft', icon: AirplaneTiltIcon },
] as const;

/*
 * Read once per page load, at module scope: the browser does not change under a
 * running page, and the notice is meant to come back on every launch, which a
 * module evaluated once per load gives for free.
 */
const STORAGE_RISK = detectStorageRisk(currentBrowserEnvironment());

function RootLayout() {
  const importOpen = useImportQueue((state) => state.items.length > 0);
  const [storageNoticeOpen, setStorageNoticeOpen] = useState(STORAGE_RISK !== null);
  const firstRun = useShouldShowWelcome();
  useAutoSync();
  useIncomingFiles();
  useDesktopShell();
  useKeyboardShortcuts();
  return (
    <div className="flex min-h-full flex-col">
      <a
        href="#main"
        className="focus:bg-accent focus:text-accent-foreground focus:rounded-control sr-only z-(--z-skip-link) focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:px-3 focus:py-2 focus:text-sm"
      >
        Skip to content
      </a>

      <header className="border-border bg-surface border-b print:hidden">
        <div className="mx-auto flex h-16 w-full max-w-6xl items-center gap-2 px-4 sm:gap-8 sm:px-6">
          {/*
            On a phone the destinations move to a labelled tab bar along the
            bottom (below). Icon-only buttons up here were what fitted 360px,
            but a map, a book and a plane are not self-explanatory on first
            use, and a thumb reaches the bottom of the screen more easily than
            the top.
          */}
          <Link to="/flights" className="shrink-0 text-sm font-semibold tracking-tight">
            Flight Data Viewer
          </Link>
          <nav aria-label="Primary" className="hidden items-center gap-1 sm:flex">
            {NAV.map(({ to, label, icon: Icon }) => (
              <Link
                key={to}
                to={to}
                /*
                  Active and inactive colours live in separate props rather than
                  overriding one another. The router concatenates class strings
                  without merging them, so `text-foreground` layered on top of
                  `text-muted-foreground` loses to whichever the stylesheet
                  happens to order last.
                */
                aria-label={label}
                className="rounded-control flex items-center gap-2 px-3 py-1.5 text-sm transition-colors duration-150"
                activeProps={{
                  className: 'bg-accent-soft text-foreground',
                  'aria-current': 'page',
                }}
                inactiveProps={{
                  className: 'text-muted-foreground hover:bg-surface-sunken hover:text-foreground',
                }}
              >
                <Icon aria-hidden="true" weight={ICON_WEIGHT} className="size-4" />
                <span>{label}</span>
                {to === '/logbook' ? <DraftBadge /> : null}
              </Link>
            ))}
          </nav>
          {/*
            The one global import affordance, at the end of the bar so it does
            not compete with the three destinations for the same glance. It
            stays in the header on a phone: importing is the first thing a
            pilot does, and a control that only exists on a desktop would make
            the phone build a viewer.
          */}
          <div className="ml-auto flex items-center gap-1">
            <ImportButton />
            <ImportMoreMenu />
            {/*
              Not a fourth primary destination — settings is a place you visit
              occasionally, not one of the three things this app is for, so it
              gets an icon button rather than a nav label competing for the
              same glance.
            */}
            <Link
              to="/settings"
              aria-label="Settings"
              className="rounded-control text-muted-foreground hover:bg-surface-sunken hover:text-foreground hidden size-9 items-center justify-center transition-colors duration-150 sm:flex"
              activeProps={{ className: 'bg-accent-soft text-foreground', 'aria-current': 'page' }}
            >
              <GearSixIcon aria-hidden="true" weight={ICON_WEIGHT} className="size-4" />
            </Link>
          </div>
        </div>
      </header>

      <main
        id="main"
        /*
          While the import results are open the page makes room for them from
          `lg` up, rather than the floating card sitting over the right-hand
          edge of the content — where the flight list keeps its Remove
          buttons and the flight page its logbook panel.
        */
        className={`mx-auto w-full max-w-6xl flex-1 px-4 py-6 sm:px-6 sm:py-10 print:max-w-none print:p-0 ${
          importOpen ? 'lg:pr-[28rem] xl:max-w-none' : ''
        }`}
      >
        {/*
          Above the page rather than on one of them: the startup check runs
          wherever the pilot happened to land, and what it found — new drafts
          waiting to be approved — is worth saying on any of the four. It
          renders nothing at all unless there is something to report.
        */}
        <UpdateBanner />
        <OpenedFileNotice />
        <AutoImportBanner />
        <Outlet />
        {/*
          Inside the content and after it, which on a phone is the only place it
          can go.

          At phone width the results panel is a full-width sheet across the
          bottom of the window, and being `fixed` it sat on top of whatever was
          down there — including the View map and Export buttons of the flight
          that had just been imported. Scrolling could not free them: the sheet
          scrolls with the window, not with the page. So below `sm` it is an
          ordinary block that takes its own space. *After* the outlet rather than
          before it, because above the content it pushed the page down instead —
          far enough that opening a flight put the map itself below the fold.
          From `sm` up it is `position: fixed` again and the floating card is
          exactly as it was.
        */}
        <ImportQueuePanel />
      </main>

      {/* Window-wide, so a file can be dropped on any page. */}
      <DropZone onFiles={openDroppedFiles} />

      {/*
        The storage notice before the first-run guide, never stacked with it. On
        a first run it matters most: a pilot who moves to the Home Screen before
        importing anything has nothing to carry across.

        It opens at once rather than waiting on the database to say whether
        this is a first run: a dialog that arrived a beat after the page took
        the page inert under whatever was being typed. The backup half joins
        it once the answer is in — a first run never shows it, so a first run
        never sees it appear.
      */}
      {STORAGE_RISK !== null && storageNoticeOpen ? (
        <StorageNoticeDialog
          risk={STORAGE_RISK}
          showBackup={firstRun === false}
          onClose={() => {
            setStorageNoticeOpen(false);
          }}
        />
      ) : null}

      <ShortcutsDialog />

      {/* Opens itself on a first run and after a reset, over whichever page that is. */}
      <WelcomeDialog deferred={storageNoticeOpen} />

      <footer className="border-border border-t pb-16 sm:pb-0 print:hidden">
        <div className="text-muted-foreground mx-auto w-full max-w-6xl px-4 py-4 text-center text-xs sm:px-6">
          {isTauri() ? 'Stored locally on this device.' : 'Stored locally in this browser.'}{' '}
          <SyncStatusLine />{' '}
          <Link to="/about" className="hover:text-foreground underline underline-offset-2">
            Privacy and data
          </Link>
        </div>
      </footer>

      <nav
        aria-label="Primary"
        className="border-border bg-surface fixed inset-x-0 bottom-0 z-(--z-sticky) grid grid-cols-4 border-t pb-[env(safe-area-inset-bottom)] sm:hidden print:hidden"
      >
        {[...NAV, { to: '/settings', label: 'Settings', icon: GearSixIcon } as const].map(
          ({ to, label, icon: Icon }) => (
            <Link
              key={to}
              to={to}
              aria-label={label}
              className="flex flex-col items-center gap-0.5 py-2 text-[0.6875rem] transition-colors duration-150"
              activeProps={{ className: 'bg-accent-soft text-foreground', 'aria-current': 'page' }}
              inactiveProps={{ className: 'text-muted-foreground' }}
            >
              <span className="relative">
                <Icon aria-hidden="true" weight={ICON_WEIGHT} className="size-5" />
                {to === '/logbook' ? (
                  <span className="absolute -top-1.5 left-3.5">
                    <DraftBadge />
                  </span>
                ) : null}
              </span>
              {label}
            </Link>
          ),
        )}
      </nav>
    </div>
  );
}

function openDroppedFiles(files: File[]) {
  void openFiles(files);
}

/**
 * Files handed in from outside the app on launch: Open With on an installed
 * web app, and Android's share sheet. The desktop app has its own route in
 * (`desktop/bridge.ts`). All three end in `openFiles`.
 */
function useIncomingFiles() {
  useEffect(() => {
    consumeLaunchQueue((files) => {
      void openFiles(files);
    });
    void collectSharedFiles().then((files) => {
      if (files.length > 0) {
        void openFiles(files);
      }
    });
  }, []);
}

/**
 * The desktop app's menu bar and its daily update check. Nothing in a browser.
 */
function useDesktopShell() {
  const navigate = useNavigate();
  useEffect(() => {
    let stop: (() => void) | undefined;
    let cancelled = false;
    const withLicense = (manual: boolean) =>
      readLicense().then((state) =>
        checkForDesktopUpdate({
          manual,
          license: state.status === 'valid' ? state.license : null,
        }),
      );
    void startDesktopBridge({
      navigate: (to, hash) => {
        void navigate({ to, ...(hash === undefined ? {} : { hash }) });
      },
      openFiles: (files) => {
        void openFiles(files);
      },
      action: (id) => {
        if (id === 'sync-now') {
          void syncNow({ interactive: true });
        } else if (id === 'shortcuts') {
          useShortcutsDialog.setState({ open: true });
        } else if (id === 'check-updates') {
          void navigate({ to: '/about' });
          void withLicense(true);
        }
      },
    }).then((unlisten) => {
      if (cancelled) {
        unlisten();
      } else {
        stop = unlisten;
      }
    });
    void withLicense(false);
    return () => {
      cancelled = true;
      stop?.();
    };
  }, [navigate]);
}

/**
 * How many logbook entries are waiting on approval, on the Logbook tab itself.
 *
 * Import drafts an entry per flight, and nothing counts until it is approved —
 * but nothing on the Flights page said so, and a pilot could import a season
 * and never learn there was a step left.
 */
function DraftBadge() {
  const count = useDraftCount();
  if (count === null || count === 0) {
    return null;
  }
  return (
    <span
      className="bg-accent text-accent-foreground tabular rounded-full px-1.5 text-[0.625rem] leading-4"
      title={`${String(count)} ${count === 1 ? 'entry' : 'entries'} awaiting approval`}
    >
      {count}
    </span>
  );
}

/**
 * The other ways in, beside the file picker: an API fetch for a flight with no
 * export yet, and a CSV from another logbook app. Both used to be found only
 * on the page that owned them — the fetch as a link beside the Flights
 * heading, the CSV import two tabs deep in the logbook.
 */
function ImportMoreMenu() {
  return (
    <Popover
      closeOnClick
      panelClassName="flex min-w-52 flex-col p-1"
      trigger={({ open, toggle }) => (
        <Button
          variant="ghost"
          size="icon"
          aria-label="More import options"
          aria-expanded={open}
          onClick={toggle}
        >
          <CaretDownIcon aria-hidden="true" weight={ICON_WEIGHT} className="size-4" />
        </Button>
      )}
    >
      <Link
        to="/flights/fetch"
        className="hover:bg-surface-sunken rounded-control px-2.5 py-1.5 text-sm"
      >
        Fetch from an API
      </Link>
      <Link
        to="/logbook/export"
        hash="import"
        className="hover:bg-surface-sunken rounded-control px-2.5 py-1.5 text-sm"
      >
        Import logbook CSV
      </Link>
    </Popover>
  );
}

function NotFound() {
  return (
    <div className="py-16 text-center">
      <h1 className="text-lg font-semibold tracking-tight">Page not found</h1>
      <p className="text-muted-foreground mx-auto mt-2 max-w-[60ch] text-sm">
        That route does not exist.
      </p>
      <Button asChild className="mt-6">
        <Link to="/flights">Go to Flights</Link>
      </Button>
    </div>
  );
}

export const Route = createRootRoute({
  component: RootLayout,
  notFoundComponent: NotFound,
});
